import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { useState } from 'react';
import { PROMPT_CATEGORIES, promptsIn, type PromptCategory } from '../model/prompts';

const SHOWN = 4;

type Props = { onPick: (prompt: string) => void; onSeeAll: (category: PromptCategory) => void };

/** Four prompts per category under the composer, with the full library one click away. */
export function PromptTabs({ onPick, onSeeAll }: Props) {
  const [category, setCategory] = useState<PromptCategory>('Forecasts');

  return (
    <Stack sx={{ gap: 1.5 }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Stack direction="row" role="tablist" aria-label="Prompt categories" sx={trackSx}>
          {PROMPT_CATEGORIES.map((c) => (
            <ButtonBase key={c} role="tab" aria-selected={c === category} onClick={() => { setCategory(c); }} sx={tabSx(c === category)}>
              {c}
            </ButtonBase>
          ))}
        </Stack>
        <ButtonBase onClick={() => { onSeeAll(category); }} sx={seeAllSx}>
          See all prompts <ArrowRight size={14} aria-hidden />
        </ButtonBase>
      </Stack>
      <Box role="tabpanel" aria-label={category} key={category} sx={gridSx}>
        {promptsIn(category).slice(0, SHOWN).map((p, i) => (
          <ButtonBase key={p.text} onClick={() => { onPick(p.text); }} sx={{ ...cellSx, animationDelay: `${i * 40}ms` }}>
            <Typography variant="body2" sx={{ flexGrow: 1, textAlign: 'left', fontSize: '0.8125rem' }}>{p.text}</Typography>
            <ArrowUpRight className="arrow" size={14} aria-hidden />
          </ButtonBase>
        ))}
      </Box>
    </Stack>
  );
}

const trackSx = { p: 0.375, gap: 0.25, borderRadius: 1.25, bgcolor: 'surface.sunken', flexWrap: 'wrap' } as const;

const tabSx = (isOn: boolean) => ({
  height: 28,
  px: 1.25,
  borderRadius: 1,
  fontSize: '0.78125rem',
  fontWeight: isOn ? 600 : 500,
  color: isOn ? 'text.primary' : 'text.secondary',
  bgcolor: isOn ? 'background.paper' : 'transparent',
  boxShadow: isOn ? '0 1px 2px rgba(26, 26, 25, 0.08)' : 'none',
  transition: 'background-color 150ms, color 150ms, box-shadow 150ms',
  '&:hover': { color: 'text.primary' },
});

const seeAllSx = { ml: 'auto', gap: 0.5, fontSize: '0.78125rem', fontWeight: 600, borderRadius: 1, px: 0.5, '&:hover svg': { transform: 'translateX(2px)' }, '& svg': { transition: 'transform 150ms' } } as const;

// A 2 x 2 grid with hairline dividers: the cells sit on the border colour with a 1px gap.
const gridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: '1px',
  bgcolor: 'divider',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1.5,
  overflow: 'hidden',
} as const;

const cellSx = {
  alignItems: 'flex-start',
  gap: 1.5,
  minHeight: 56,
  px: 1.75,
  py: 1.5,
  bgcolor: 'background.paper',
  transition: 'background-color 120ms',
  '@keyframes cellIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'none' } },
  animation: 'cellIn 240ms ease-out both',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  '& .arrow': { flexShrink: 0, mt: 0.25, color: 'text.secondary', transition: 'transform 150ms, color 150ms' },
  '&:hover': { bgcolor: 'surface.sidebar', '& .arrow': { color: 'primary.main', transform: 'translate(1px, -1px)' } },
} as const;
